import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  User,
  Package,
  IndianRupee,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Printer,
  X,
  Loader2,
  Truck,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rupee = (n) => `₹${(n || 0).toFixed(2)}`;
const todayStr = () => new Date().toISOString().split("T")[0];

const COMPANY_SETTINGS = {
  name: "M. R. B. SAnitary & Ceramic",
  address:
    "GF, Shed NO. 4,5,6 Survey No. 1525/83, TP.54, FP No.29, Jay Ambe Estate, Ognaj Circle, Opp. Purohit Hotel, Ahmedabad",
  phone: "9737012222",
  gstin: "24AUAPJ3156A1ZC",
  state: "Gujarat, Code : 24",
  email: "mrbceramic406@gmail.com",
  pan: "AUAPJ3156A",
};

export default function POS() {
  const { user } = useAuth();
  const { t } = useTranslation();

  // Products & Cart
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [cart, setCart] = useState([]);

  // Customer
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");

  // Payment & Delivery
  const [amountPaid, setAmountPaid] = useState(0);
  const [scheduledDate, setScheduledDate] = useState("");
  const [enableScheduledDelivery, setEnableScheduledDelivery] = useState(false);

  // UI State
  const [loading, setLoading] = useState(false);
  const [processingOrder, setProcessingOrder] = useState(false);
  const [waitingOrder, setWaitingOrder] = useState(null);

  const totalAmount = useMemo(
    () => cart.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cart],
  );
  const balanceDue = Math.max(0, totalAmount - amountPaid);
  const isFullPayment = amountPaid >= totalAmount;
  const isPartialPayment = amountPaid > 0 && amountPaid < totalAmount;

  // ─── Fetch Products ────────────────────────────────────────────
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/products`, {
        headers: { Authorization: `Bearer ${user.access_token}` },
      });
      setProducts(res.data);
      setFilteredProducts(res.data);
    } catch (err) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  // ─── Search Products ───────────────────────────────────────────
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredProducts(products);
      return;
    }
    const q = searchQuery.toLowerCase();
    setFilteredProducts(
      products.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          p.brand.toLowerCase().includes(q),
      ),
    );
  }, [searchQuery, products]);

  // ─── Cart Operations ───────────────────────────────────────────
  const addToCart = (product) => {
    const existing = cart.find((item) => item.id === product.id);
    if (existing) {
      setCart(
        cart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        ),
      );
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    toast.success(`${product.name} added to cart`);
  };

  const updateQuantity = (id, delta) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.id === id
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item,
        )
        .filter((item) => item.quantity > 0),
    );
  };

  const removeFromCart = (id) => {
    setCart(cart.filter((item) => item.id !== id));
  };

  const clearCart = () => {
    setCart([]);
    setCustomerName("");
    setCustomerPhone("");
    setAmountPaid(0);
    setScheduledDate("");
    setEnableScheduledDelivery(false);
  };

  // ─── Process Order ─────────────────────────────────────────────
  const processOrder = async () => {
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    if (!customerName.trim()) {
      toast.error("Please enter customer name");
      return;
    }

    if (amountPaid <= 0) {
      toast.error("Please enter amount paid");
      return;
    }

    if (enableScheduledDelivery && !scheduledDate) {
      toast.error("Please select a delivery date");
      return;
    }

    try {
      setProcessingOrder(true);
      
      const targetStatus = enableScheduledDelivery
        ? isPartialPayment
          ? "pending_delivery_partial"
          : "pending_delivery"
        : balanceDue > 0
          ? "pending"
          : "completed";

      const orderData = {
        customer_name: customerName,
        customer_phone: customerPhone,
        items: cart.map((item) => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          price: item.price,
          total: item.price * item.quantity,
        })),
        subtotal: totalAmount,
        gst_amount: 0,
        total: totalAmount,
        payment_method: "Credit",
        amount_paid: amountPaid,
        balance_due: balanceDue,
        scheduled_delivery_date: enableScheduledDelivery ? scheduledDate : null,
        status: "Godown Pending Approval",
        narration: targetStatus, // Store the target status in narration for Main POS to use
        created_by: user.id,
      };

      const res = await axios.post(`${API_URL}/orders`, orderData, {
        headers: { Authorization: `Bearer ${user.access_token}` },
      });

      toast.success("Approval requested!");
      setWaitingOrder(res.data);
    } catch (err) {
      console.error("Order creation error:", err);
      toast.error(err.response?.data?.detail || "Failed to create order");
    } finally {
      setProcessingOrder(false);
    }
  };

  // ─── Poll for Approval ─────────────────────────────────────────
  useEffect(() => {
    let interval;
    if (waitingOrder) {
      interval = setInterval(async () => {
        try {
          const res = await axios.get(`${API_URL}/orders/${waitingOrder.id}`, {
            headers: { Authorization: `Bearer ${user.access_token}` },
          });
          if (res.data && res.data.status !== "Godown Pending Approval") {
            if (res.data.status === "rejected") {
              toast.error("Order was rejected by Admin.");
            } else {
              toast.success("Order Approved!");
              printInvoice(res.data);
              clearCart();
            }
            setWaitingOrder(null);
          }
        } catch (err) {
          console.error("Polling error:", err);
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [waitingOrder, user.access_token]);

  // ─── Print Invoice ─────────────────────────────────────────────
  const printInvoice = (order) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Please allow popups to print invoice");
      return;
    }

    const invoiceHTML = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Invoice - ${order.invoice_no || order.id}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: 'Courier New', monospace; 
      font-size: 11px; 
      line-height: 1.4;
      padding: 20px;
      max-width: 80mm;
    }
    .header { text-align: center; margin-bottom: 12px; border-bottom: 2px solid #000; padding-bottom: 8px; }
    .company-name { font-weight: bold; font-size: 14px; }
    .section { margin: 10px 0; }
    .label { font-weight: bold; }
    table { width: 100%; border-collapse: collapse; margin: 8px 0; }
    th, td { padding: 4px 2px; text-align: left; }
    th { border-bottom: 1px solid #000; font-weight: bold; }
    .right { text-align: right; }
    .total-row { border-top: 2px solid #000; font-weight: bold; padding-top: 4px; }
    .alert-box { 
      border: 2px solid #000; 
      padding: 8px; 
      margin: 10px 0; 
      font-weight: bold;
      text-align: center;
    }
    .footer { text-align: center; margin-top: 12px; border-top: 1px solid #000; padding-top: 8px; }
    @media print {
      body { padding: 0; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="company-name">${COMPANY_SETTINGS.name}</div>
    <div>${COMPANY_SETTINGS.address}</div>
    <div>Phone: ${COMPANY_SETTINGS.phone}</div>
    <div>GSTIN: ${COMPANY_SETTINGS.gstin}</div>
  </div>

  <div class="section">
    <div><span class="label">Invoice No:</span> ${order.invoice_no || order.id}</div>
    <div><span class="label">Date:</span> ${new Date().toLocaleString("en-IN")}</div>
    <div><span class="label">Customer:</span> ${order.customer_name}</div>
    ${order.customer_phone ? `<div><span class="label">Phone:</span> ${order.customer_phone}</div>` : ""}
  </div>

  <table>
    <thead>
      <tr>
        <th>Item</th>
        <th class="right">Qty</th>
        <th class="right">Price</th>
        <th class="right">Total</th>
      </tr>
    </thead>
    <tbody>
      ${order.items
        .map(
          (item) => `
        <tr>
          <td>${item.product_name}</td>
          <td class="right">${item.quantity}</td>
          <td class="right">${rupee(item.price)}</td>
          <td class="right">${rupee(item.total)}</td>
        </tr>
      `,
        )
        .join("")}
    </tbody>
  </table>

  <div class="total-row">
    <div style="display: flex; justify-content: space-between;">
      <span>Total Amount:</span>
      <span>${rupee(order.total)}</span>
    </div>
    <div style="display: flex; justify-content: space-between;">
      <span>Amount Paid:</span>
      <span>${rupee(order.amount_paid || 0)}</span>
    </div>
    ${
      order.balance_due > 0
        ? `
    <div style="display: flex; justify-content: space-between; color: #d00;">
      <span>Balance Due:</span>
      <span>${rupee(order.balance_due)}</span>
    </div>
    `
        : ""
    }
  </div>

  ${
    order.scheduled_delivery_date
      ? `
  <div class="alert-box">
    <div>⚠️ SCHEDULED DELIVERY ⚠️</div>
    <div>Delivery Date: ${new Date(order.scheduled_delivery_date).toLocaleDateString("en-IN")}</div>
    ${
      order.balance_due > 0
        ? `<div>Balance ${rupee(order.balance_due)} to be collected on delivery</div>`
        : `<div>Fully Paid - Customer will pick up goods</div>`
    }
  </div>
  `
      : ""
  }

  <div class="footer">
    <div>Thank you for your business!</div>
    <div class="no-print" style="margin-top: 10px;">
      <button onclick="window.print()" style="padding: 8px 16px; cursor: pointer;">Print Invoice</button>
    </div>
  </div>

  <script>
    // Auto-print on load
    setTimeout(() => window.print(), 500);
  </script>
</body>
</html>
    `;

    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
  };

  return (
    <div className="h-screen flex flex-col bg-stone-950">
      {/* Header */}
      <div className="bg-stone-900 border-b border-stone-800 p-4">
        <h1 className="text-xl font-bold text-stone-100">
          Point of Sale - Credit Only
        </h1>
        <p className="text-sm text-stone-400">
          Schedule deliveries and manage credit payments
        </p>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left: Products */}
        <div className="flex-1 flex flex-col border-r border-stone-800">
          {/* Search */}
          <div className="p-4 border-b border-stone-800">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-stone-900 border border-stone-700 rounded-lg text-stone-100 placeholder-stone-500 focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {filteredProducts.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => addToCart(product)}
                    disabled={product.stock <= 0}
                    className="bg-stone-900 border border-stone-800 rounded-lg p-3 hover:border-emerald-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-left"
                  >
                    <div className="font-medium text-stone-100 text-sm mb-1 line-clamp-2">
                      {product.name}
                    </div>
                    <div className="text-xs text-stone-400 mb-2">
                      {product.category} · {product.brand}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-emerald-400 font-bold">
                        {rupee(product.price)}
                      </span>
                      <span className="text-xs text-stone-500">
                        Stock: {product.stock}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: Cart & Checkout */}
        <div className="w-96 flex flex-col bg-stone-900">
          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-4 border-b border-stone-800">
            <div className="flex items-center gap-2 mb-4">
              <ShoppingCart className="w-5 h-5 text-emerald-400" />
              <h2 className="text-lg font-bold text-stone-100">
                Cart ({cart.length})
              </h2>
            </div>

            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-stone-500">
                <ShoppingCart className="w-12 h-12 mb-2" />
                <p>Cart is empty</p>
              </div>
            ) : (
              <div className="space-y-2">
                {cart.map((item) => (
                  <div
                    key={item.id}
                    className="bg-stone-800 rounded-lg p-3 border border-stone-700"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="font-medium text-stone-100 text-sm">
                          {item.name}
                        </div>
                        <div className="text-xs text-stone-400">
                          {rupee(item.price)} each
                        </div>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-rose-400 hover:text-rose-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.id, -1)}
                          className="w-7 h-7 flex items-center justify-center bg-stone-700 rounded hover:bg-stone-600 text-stone-100"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-stone-100 font-medium">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, 1)}
                          className="w-7 h-7 flex items-center justify-center bg-stone-700 rounded hover:bg-stone-600 text-stone-100"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="text-emerald-400 font-bold">
                        {rupee(item.price * item.quantity)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Checkout Form */}
          <div className="p-4 space-y-3">
            {/* Customer Details */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-stone-300">
                <User className="w-4 h-4" />
                Customer Details
              </div>
              <input
                type="text"
                placeholder="Customer Name *"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="w-full px-3 py-2 bg-stone-800 border border-stone-700 rounded-lg text-stone-100 placeholder-stone-500 focus:outline-none focus:border-emerald-500 text-sm"
              />
              <input
                type="tel"
                placeholder="Phone (optional)"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                className="w-full px-3 py-2 bg-stone-800 border border-stone-700 rounded-lg text-stone-100 placeholder-stone-500 focus:outline-none focus:border-emerald-500 text-sm"
              />
            </div>

            {/* Total Amount */}
            <div className="bg-stone-800 rounded-lg p-3 border border-stone-700">
              <div className="flex justify-between items-center text-lg font-bold">
                <span className="text-stone-300">Total Amount:</span>
                <span className="text-emerald-400">{rupee(totalAmount)}</span>
              </div>
            </div>

            {/* Amount Paid */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-stone-300">
                <IndianRupee className="w-4 h-4" />
                Amount Paid (Credit)
              </div>
              <input
                type="number"
                placeholder="Enter amount paid..."
                value={amountPaid || ""}
                onChange={(e) => setAmountPaid(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-stone-800 border border-stone-700 rounded-lg text-stone-100 placeholder-stone-500 focus:outline-none focus:border-emerald-500 text-sm"
                min="0"
                step="0.01"
              />
            </div>

            {/* Balance Due Alert */}
            {balanceDue > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <div className="flex items-center gap-2 text-amber-400 text-sm font-medium">
                  <AlertCircle className="w-4 h-4" />
                  Balance Due: {rupee(balanceDue)}
                </div>
              </div>
            )}

            {/* Scheduled Delivery */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enableScheduledDelivery}
                  onChange={(e) => setEnableScheduledDelivery(e.target.checked)}
                  className="w-4 h-4 rounded border-stone-700 bg-stone-800 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0"
                />
                <div className="flex items-center gap-2 text-sm font-medium text-stone-300">
                  <Truck className="w-4 h-4" />
                  Schedule Delivery
                </div>
              </label>

              {enableScheduledDelivery && (
                <div className="space-y-2 pl-6">
                  <input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={todayStr()}
                    className="w-full px-3 py-2 bg-stone-800 border border-stone-700 rounded-lg text-stone-100 focus:outline-none focus:border-emerald-500 text-sm"
                  />
                  {isPartialPayment && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-2 text-xs text-blue-400">
                      <div className="font-medium mb-1">
                        Partial Payment Delivery
                      </div>
                      <div>
                        Customer will pay {rupee(balanceDue)} on delivery
                      </div>
                    </div>
                  )}
                  {isFullPayment && (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2 text-xs text-emerald-400">
                      <div className="font-medium mb-1">
                        Fully Paid Delivery
                      </div>
                      <div>Customer will pick up goods on scheduled date</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={clearCart}
                disabled={cart.length === 0}
                className="flex-1 py-2.5 bg-stone-800 border border-stone-700 rounded-lg text-stone-300 hover:bg-stone-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                Clear
              </button>
              <button
                onClick={processOrder}
                disabled={processingOrder || cart.length === 0}
                className="flex-1 py-2.5 bg-emerald-600 rounded-lg text-white hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center justify-center gap-2"
              >
                {processingOrder ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Request Approval
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Waiting Modal */}
      <AnimatePresence>
        {waitingOrder && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-sm bg-stone-900 border border-stone-800 rounded-2xl shadow-2xl p-6 text-center overflow-hidden relative"
            >
              {/* Spinning background effect */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-amber-500/20 blur-3xl rounded-full animate-pulse" />
              
              <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4 relative z-10" />
              <h2 className="text-xl font-bold text-stone-100 mb-2 relative z-10">Waiting for Approval</h2>
              <p className="text-stone-400 text-sm mb-6 relative z-10">
                Please ask the Main POS Admin to approve order <br/>
                <span className="font-mono font-bold text-amber-400">{waitingOrder.invoice_number || waitingOrder.id}</span>
              </p>
              
              <button
                onClick={() => setWaitingOrder(null)}
                className="w-full py-2.5 bg-stone-800 hover:bg-stone-700 text-stone-300 rounded-lg transition-colors font-semibold relative z-10"
              >
                Cancel Request
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
